type Key = string | number | bigint | boolean | Date | null | undefined | Key[]

function rank(v: unknown): number {
	// defines an order across types: undefined, null, NaN, then values
	if (v === undefined) return 0
	if (v === null) return 1
	if (typeof v === 'number' && Number.isNaN(v)) return 2
	return 3
}

function compare(a: Key, b: Key): number {
	if (Array.isArray(a) && Array.isArray(b)) {
		const len = Math.min(a.length, b.length)
		for (let i = 0; i < len; i++) {
			const c = compare(a[i], b[i])
			if (c !== 0) return c
		}
		return a.length - b.length
	}
	const ra = rank(a), rb = rank(b)
	if (ra !== rb || ra < 3) return ra - rb
	return a! < b! ? -1 : a! > b! ? 1 : 0
}

function sortWith<T>(array: readonly T[], key: (v: T) => Key, dir: 1 | -1): T[] {
	// compute each key once, then sort
	return array
		.map((value, i) => ({ value, key: key(value), i }))
		.sort((x, y) => dir * compare(x.key, y.key) || x.i - y.i)
		.map(x => x.value)
}

export const sortBy = <T>(array: readonly T[], key: (v: T) => Key) => sortWith(array, key, 1)
export const sortByDesc = <T>(array: readonly T[], key: (v: T) => Key) => sortWith(array, key, -1)

export function distinct<T>(array: readonly T[]): T[] {
	return [...new Set(array)] // SameValueZero: handles NaN, keeps first occurrence
}

export function distinctBy<T>(array: readonly T[], key: (v: T) => unknown): T[] {
	const seen = new Set<unknown>()
	return array.filter(x => {
		let k = key(x)
		if (Array.isArray(k)) k = JSON.stringify(k) // make tuple keys comparable by value
		if (seen.has(k)) return false
		seen.add(k)
		return true
	})
}
